// Pantalla intermedia post-pago — SOLO argentinos (Rebill). Tras un pago exitoso,
// el "Continuar" del checkout trae acá en vez de ir directo al estudio. Ofrece
// cargar los datos fiscales para facturar por AFIP (este pago y los próximos).
// "Sí" → /facturacion (portal, con contexto postPago). "No" → entra al estudio.
// Es puro front: no emite ni guarda nada; solo rutea. El gateo AR real vive en el
// punto de entrada (el "Continuar" del checkout solo llega acá si isArgentina) y
// en /facturacion (que redirige al home a los no-AR).
// Ver ADR-2026-08-13-facturacion-pantalla-post-pago-ar.

import { useEffect } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowRight, FileText, Percent, Receipt, Repeat } from 'lucide-react';
import { PantallaPostPago } from '../components/postPago/PantallaPostPago';
import {
  destinoEsElCuestionario,
  PARAM_HOME_POST_PAGO,
  resolverHomePostPago,
  rutaConHomePostPago,
} from '../lib/checkoutPlans';

export default function FacturacionOferta() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  // Esta pantalla es la que se comía el destino del alta por link: con AFIP en producción
  // TODO argentino que paga pasa por acá, y sus dos salidas resolvían el estudio por su
  // cuenta. Ahora respeta el destino que mandó quien cobró, y lo pasa al portal.
  // Ver ADR-2026-09-22-el-destino-post-pago-sobrevive-la-cadena.
  const { homeDelLlamador, homePath } = resolverHomePostPago(
    searchParams.get(PARAM_HOME_POST_PAGO),
  );

  // Esta pantalla solo tiene sentido viniendo de un pago (postPago en el state).
  // Si alguien entra directo por URL o refresca (se pierde el state), lo mandamos
  // al estudio en vez de mostrarle una oferta suelta. `?preview` la fuerza para
  // poder revisar el diseño / testear sin tener que pagar.
  const preview = searchParams.get('preview') !== null;
  const puedeVerse =
    preview || (location.state as { postPago?: boolean } | null)?.postPago === true;
  useEffect(() => {
    if (!puedeVerse) navigate(homePath, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (!puedeVerse) return null;

  const configurarFacturacion = () =>
    // postPago va por query param (?postPago=1): sobrevive un refresh de /facturacion (el
    // location.state se pierde al recargar), así el portal sigue mostrando "Entrar a Atlas". Greptile PR#67.
    // El destino final viaja al lado, por el MISMO motivo: el portal es un formulario
    // largo y recargarlo no puede costarle el cuestionario al que acaba de pagar.
    navigate(rutaConHomePostPago('/facturacion?postPago=1', homeDelLlamador));
  const entrarAlEstudio = () => navigate(homePath, { replace: true });

  return (
    <PantallaPostPago
      icono={<Receipt className="h-8 w-8" />}
      insignia="Facturación AFIP"
      titulo="¿Querés la factura de tu plan?"
      texto="Cargá tus datos fiscales una sola vez y te generamos la factura de cada pago, sin que tengas que pedirla."
      puntos={[
        { icono: <FileText className="h-4 w-4" />, texto: 'Cargás tu CUIT y tu razón social una sola vez.' },
        { icono: <Repeat className="h-4 w-4" />, texto: 'Te generamos la factura AFIP de este pago y de cada renovación.' },
        { icono: <Percent className="h-4 w-4" />, texto: 'Si sos Responsable Inscripto, recibís Factura A y descontás el IVA.' },
      ]}
      primaria={{ label: 'Sí, configurar facturación', onClick: configurarFacturacion, icono: <ArrowRight className="h-4 w-4" /> }}
      secundaria={{
        // El que vino del alta por link NO entra al estudio acá: le falta el cuestionario y la
        // salida lo lleva ahí. Prometerle el estudio sería mentirle justo después de cobrarle.
        // Ver ADR-2026-09-22-el-destino-post-pago-sobrevive-la-cadena.
        label: destinoEsElCuestionario(homeDelLlamador) ? 'Ahora no, continuar' : 'Ahora no, entrar al estudio',
        onClick: entrarAlEstudio,
      }}
      nota="Podés activarla cuando quieras desde Configuración."
    />
  );
}
